import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import moment, {Moment} from 'moment';
import {v4 as uuidv4} from 'uuid';
import {Alert, Button, Divider, Grid, Stack, Typography} from '@mui/material';
import {
  AttendanceTrackerRegistryRecordError,
  AttendanceTrackerRegistrySession,
  AttendanceTrackerRegistryUser,
} from '../../backend/responses/attendance-tracker-registry';
import {useAuth} from '../../contexts/auth/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {ScreenPaths} from '../../enums';
import {translations} from '../../i18n/translations';
import {EntityAttendanceTrackerRegistryRepository} from '../../repositories/entity-attendance-tracker-registry';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown, Option} from '../flat-select-dropdown/flat-select-dropdown';
import {
  DateTimeFields,
  nowToTheMinute,
  REASON_MAX_LENGTH,
  SummaryRow,
} from './attendance-registry-form-parts';
import {Modal} from '../modal/modal';

type Kind = 'session' | 'entry' | 'exit';

type AttendanceRegistryAddDialogProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  /** Called after the records were registered, so the list can be loaded again */
  onRegistered: () => void;
};

/**
 * Registers an entry, an exit, or a whole session on behalf of another professional, for moments that can be in
 * the past (e.g. a forgotten exit). Nobody can register on behalf of themselves.
 */
export default function AttendanceRegistryAddDialog({
  open,
  onClose,
  entityId,
  onRegistered,
}: AttendanceRegistryAddDialogProps) {
  const {t} = useTranslation();
  const history = useHistory();
  const {state} = useAuth();
  const snackBarContext = useSnackBar();
  const texts = translations.attendance_tracker_registry_screen.add;
  const sessionTexts = translations.daily_care_log.attendance_tracker.session;
  // How the Daily Care Log is called in the menu of the user
  const dailyCareLogName = t(translations.user_menu.dailyCareLog);

  const currentUser = state.session?.currentUser;
  // Only the professionals that must or can track attendance can have records registered on their behalf
  const [professionals, setProfessionals] = useState<AttendanceTrackerRegistryUser[] | undefined>(
    undefined
  );

  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [professional, setProfessional] = useState<Option | undefined>(undefined);
  // The open session of the professional, if there is one: then only the missing exit can be registered
  const [openSession, setOpenSession] = useState<AttendanceTrackerRegistrySession | undefined>(
    undefined
  );
  const [isCheckingProfessional, setIsCheckingProfessional] = useState(false);
  const [kind, setKind] = useState<'session' | 'entry'>('session');
  const [entryAt, setEntryAt] = useState<Moment>(() => nowToTheMinute().subtract(1, 'hour'));
  const [exitAt, setExitAt] = useState<Moment>(() => nowToTheMinute());
  const [reason, setReason] = useState('');
  const [hasTriedToContinue, setHasTriedToContinue] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  // Generated once for each registration, so a retry does not register it twice
  const clientRequestIdRef = useRef(uuidv4());

  const isSelf = !!professional && professional.key === currentUser?.id;
  const effectiveKind: Kind = openSession ? 'exit' : kind;
  const needsEntry = effectiveKind !== 'exit';
  const needsExit = effectiveKind !== 'entry';

  // Starts from scratch every time it is opened
  useEffect(() => {
    if (!open) {
      return;
    }

    setStep('form');
    setProfessional(undefined);
    setOpenSession(undefined);
    setKind('session');
    setEntryAt(nowToTheMinute().subtract(1, 'hour'));
    setExitAt(nowToTheMinute());
    setReason('');
    setHasTriedToContinue(false);
    setServerError(undefined);
    clientRequestIdRef.current = uuidv4();

    let isCancelled = false;
    setProfessionals(undefined);
    EntityAttendanceTrackerRegistryRepository.instance
      .getProfessionals(entityId)
      .then((items) => !isCancelled && setProfessionals(items))
      .catch((e) => {
        console.error(e);
        if (!isCancelled) {
          setProfessionals([]);
          setServerError(t(texts.error_generic));
        }
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId]);

  // What the professional has registered: an open session means that the exit is what is missing
  const checkProfessional = useCallback(
    async (userId: string) => {
      setIsCheckingProfessional(true);
      try {
        const result = await EntityAttendanceTrackerRegistryRepository.instance.getSessions(
          entityId,
          moment().startOf('day').toISOString(),
          moment().endOf('day').toISOString(),
          userId
        );
        setOpenSession(result.openSessions[0]);
      } catch (e) {
        console.error(e);
        setOpenSession(undefined);
        setServerError(t(texts.error_generic));
      } finally {
        setIsCheckingProfessional(false);
      }
    },
    [entityId, t, texts.error_generic]
  );

  const professionalOptions = useMemo<Option[]>(
    () =>
      (professionals ?? []).map((user) => ({
        key: user.id,
        label: user.name,
        picture: user.picture || user.name,
      })),
    [professionals]
  );

  const handleProfessionalChange = (option: Option) => {
    setProfessional(option.key ? option : undefined);
    setOpenSession(undefined);
    setServerError(undefined);
    if (option.key && option.key !== currentUser?.id) {
      checkProfessional(option.key);
    }
  };

  // Mistakes that can be told without asking the backend
  const errors = useMemo(() => {
    const now = moment();

    return {
      entry: needsEntry && entryAt.isAfter(now) ? t(texts.validation_future) : undefined,
      exit: !needsExit
        ? undefined
        : exitAt.isAfter(now)
          ? t(texts.validation_future)
          : needsEntry && !exitAt.isAfter(entryAt)
            ? t(texts.validation_exit_before_entry)
            : openSession && !exitAt.isAfter(moment(openSession.entry.registeredAt))
              ? t(texts.validation_exit_before_open_entry)
              : undefined,
      reason: reason.trim().length === 0 ? t(texts.validation_reason_required) : undefined,
    };
  }, [needsEntry, needsExit, entryAt, exitAt, openSession, reason, t, texts]);

  const canContinue =
    !!professional &&
    !isSelf &&
    !isCheckingProfessional &&
    !errors.entry &&
    !errors.exit &&
    !errors.reason;

  const handleContinue = () => {
    setHasTriedToContinue(true);
    if (canContinue) {
      setServerError(undefined);
      setStep('confirm');
    }
  };

  const errorMessage = (error: unknown): string => {
    const code = axios.isAxiosError<AttendanceTrackerRegistryRecordError>(error)
      ? error.response?.data?.code
      : undefined;

    switch (code) {
      case 'future':
        return t(texts.error_future);
      case 'order':
        return t(texts.error_order);
      case 'overlap':
        return t(texts.error_overlap);
      case 'type':
        return t(texts.error_type);
      case 'conflict':
        return t(texts.error_conflict);
      case 'professional-not-tracking-attendance':
        return t(texts.error_not_tracking);
      case 'self-registration-not-allowed':
        return t(texts.self_warning, {app: dailyCareLogName});
      default:
        return t(texts.error_generic);
    }
  };

  const handleRegister = async () => {
    if (!professional) {
      return;
    }

    setIsSaving(true);
    setServerError(undefined);
    try {
      await EntityAttendanceTrackerRegistryRepository.instance.registerRecords(entityId, {
        userId: professional.key,
        entryAt: needsEntry ? entryAt.toISOString() : undefined,
        exitAt: needsExit ? exitAt.toISOString() : undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        reason: reason.trim(),
        clientRequestId: clientRequestIdRef.current,
      });

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(texts.success),
      });
      onRegistered();
      onClose();
    } catch (e) {
      console.error(e);
      setServerError(errorMessage(e));
      // What the professional has registered may have changed: check again and let the user confirm the data
      setStep('form');
      if (professional) checkProfessional(professional.key);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (value: Moment) => value.format('DD/MM/YYYY HH:mm');

  const kindOptions: Option[] = [
    {key: 'session', label: t(texts.kind_session)},
    {key: 'entry', label: t(texts.kind_entry)},
  ];

  return (
    <Modal
      id="attendance-registry-add"
      title={t(step === 'form' ? texts.title : texts.confirm_title)}
      open={open}
      onClose={onClose}
      preventCloseClick={isSaving}
      actionButton={{type: 'close'}}
      fullWidth
      maxWidth="sm"
      actions={
        step === 'form'
          ? [
              {label: t(texts.cancel), type: 'destructive', onClick: onClose},
              {
                label: t(texts.continue),
                type: 'nondestructive',
                disabled: hasTriedToContinue ? !canContinue : !professional || isSelf,
                onClick: handleContinue,
              },
            ]
          : [
              {
                label: t(texts.back),
                type: 'destructive',
                disabled: isSaving,
                onClick: () => setStep('form'),
              },
              {
                label: t(texts.confirm),
                type: 'nondestructive',
                loading: isSaving,
                onClick: handleRegister,
              },
            ]
      }
    >
      <Stack gap="20px">
        {serverError && <Alert severity="error">{serverError}</Alert>}

        {step === 'form' ? (
          <>
            <FlatSelectDropdown
              role="user"
              label={t(texts.professional)}
              placeholder={t(texts.professional_placeholder)}
              required
              loading={!professionals}
              options={professionalOptions}
              value={professional}
              onChange={handleProfessionalChange}
            />
            {/* Why the list can be shorter than the team */}
            <Typography
              sx={{fontSize: '13px', marginTop: '-12px', color: 'var(--dcm-gray-general)'}}
            >
              {t(
                professionals?.length === 0 ? texts.professionals_empty : texts.professionals_hint
              )}
            </Typography>

            {isSelf && (
              <Alert
                severity="warning"
                sx={{'& .MuiAlert-message': {width: '100%', paddingRight: '8px'}}}
              >
                <Stack gap="12px">
                  <Typography sx={{fontSize: '14px'}}>
                    {t(texts.self_warning, {app: dailyCareLogName})}
                  </Typography>
                  {/* Below the text, on the right, so it has room to be read */}
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    sx={{width: 'auto', alignSelf: 'flex-end', whiteSpace: 'nowrap'}}
                    onClick={() => {
                      onClose();
                      history.push(
                        `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_AttendanceTracker}`
                      );
                    }}
                  >
                    {t(texts.self_action, {app: dailyCareLogName})}
                  </Button>
                </Stack>
              </Alert>
            )}

            {professional && !isSelf && isCheckingProfessional && (
              <Typography sx={{fontSize: '14px', color: 'var(--dcm-gray-general)'}}>
                {t(texts.checking)}
              </Typography>
            )}

            {professional && !isSelf && !isCheckingProfessional && (
              <>
                {openSession ? (
                  <Alert severity="info">
                    {t(texts.open_session_info, {
                      date: formatDateTime(moment(openSession.entry.registeredAt)),
                    })}
                  </Alert>
                ) : (
                  <FlatSelectDropdown
                    label={t(texts.kind_label)}
                    options={kindOptions}
                    value={kindOptions.find(({key}) => key === kind)}
                    onChange={(option) => setKind(option.key as 'session' | 'entry')}
                  />
                )}

                <Grid container columnSpacing={2} rowSpacing={3}>
                  {needsEntry && (
                    <DateTimeFields
                      dateLabel={t(texts.entry_date)}
                      timeLabel={t(texts.entry_time)}
                      value={entryAt}
                      errors={hasTriedToContinue ? errors.entry : undefined}
                      onChange={setEntryAt}
                    />
                  )}
                  {needsExit && (
                    <DateTimeFields
                      dateLabel={t(texts.exit_date)}
                      timeLabel={t(texts.exit_time)}
                      value={exitAt}
                      errors={hasTriedToContinue ? errors.exit : undefined}
                      onChange={setExitAt}
                    />
                  )}
                </Grid>

                <FlatInput
                  label={t(texts.reason)}
                  placeholder={t(texts.reason_placeholder)}
                  required
                  multiline
                  minRows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
                  counter={{current: reason.length, max: REASON_MAX_LENGTH}}
                  errors={hasTriedToContinue ? errors.reason : undefined}
                />

                <Alert severity="info">
                  {t(texts.notice, {
                    admin: currentUser?.name,
                    professional: professional.label,
                  })}
                </Alert>
              </>
            )}
          </>
        ) : (
          <>
            <Typography sx={{fontSize: '14px'}}>{t(texts.confirm_description)}</Typography>
            <Divider />
            <SummaryRow label={t(texts.professional)} value={professional?.label} />
            <SummaryRow
              label={t(texts.summary_record)}
              value={t(
                effectiveKind === 'session'
                  ? texts.kind_session
                  : effectiveKind === 'entry'
                    ? texts.kind_entry
                    : texts.kind_exit
              )}
            />
            {needsEntry && (
              <SummaryRow label={t(sessionTexts.entry)} value={formatDateTime(entryAt)} />
            )}
            {needsExit && (
              <SummaryRow label={t(sessionTexts.exit)} value={formatDateTime(exitAt)} />
            )}
            <SummaryRow label={t(texts.reason)} value={reason.trim()} />
            <Alert severity="info">
              {t(texts.notice, {admin: currentUser?.name, professional: professional?.label})}
            </Alert>
          </>
        )}
      </Stack>
    </Modal>
  );
}
